import React, { useEffect, useState } from 'react';
import { Calendar, TrendingUp, Users, Eye, Clock, Target, AlertCircle, BarChart, MessageCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMatomoStats, useMatomoPageStats, useMatomoVisitorTrend, usePageMindUsage } from '@/lib/api/matomo-service';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '@/config/api';
import { toast } from '@/components/ui/use-toast';
import ChatbotAnalyticsEmbed from '../ChatbotAnalyticsEmbed';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import { useOnboardingTracking } from '@/hooks/useOnboardingTracking';

interface Site {
  _id: string;
  url: string;
  name: string;
  status: string;
}

const MatomoAnalytics: React.FC = () => {
  const [period, setPeriod] = React.useState('year');
  const [date, setDate] = React.useState('year');
  const [agentInsights, setAgentInsights] = React.useState<Array<{title: string, content: string, type: string}>>([]);
  const [currentInsightIndex, setCurrentInsightIndex] = React.useState(0);
  const [interpretationLoading, setInterpretationLoading] = React.useState(false);
  const [imageError, setImageError] = React.useState(false);
  const { selectedSite } = useSite();
  const { trackOnboardingStep } = useOnboardingTracking();
  const navigate = useNavigate();
  
  // Track "learn_analytics" onboarding step when component mounts
  React.useEffect(() => {
    if (selectedSite) {
      trackOnboardingStep('learn_analytics', { timestamp: new Date().toISOString() });
    }
  }, [selectedSite, trackOnboardingStep]);
  
  // Determine the date range for visitor trend based on selected period
  const getTrendDateRange = () => {
    switch (period) {
      case 'day':
        return 'last7'; // Show last 7 days for context
      case 'week':
        return 'week'; // Show current week
      case 'month':
        return 'month'; // Show current month days
      case 'year':
        return 'year'; // Show current year by month
      case 'range':
        return date; // Use the date value for range periods
      default:
        return 'last7';
    }
  };
  
  // Determine the period for the graph (always show daily except for year)
  const getGraphPeriod = () => {
    return period === 'year' ? 'month' : 'day';
  };
  
  const { data: stats, isLoading: statsLoading, error: statsError } = useMatomoStats(selectedSite?.url, period, date);
  const { data: pageStats, isLoading: pagesLoading } = useMatomoPageStats(selectedSite?.url, period, date);
  const { data: visitorTrend, isLoading: trendLoading } = useMatomoVisitorTrend(selectedSite?.url, getGraphPeriod(), getTrendDateRange());
  const { data: pagemindUsage, isLoading: usageLoading } = usePageMindUsage(selectedSite?.url, period, date);

  // Set page title and track onboarding
  useEffect(() => {
    document.title = 'Analytics - PageMind Admin';
    
    // Track analytics page visit for onboarding
    if (selectedSite?._id) {
      authFetch('/onboarding/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite._id,
          activity: {
            type: 'analytics_visited',
            path: '/analytics'
          }
        })
      }).catch(err => console.error('Failed to track analytics visit:', err));
    }
  }, [selectedSite]);

  // Site selection is now handled by global context

  // Generate AI interpretation when stats change
  useEffect(() => {
    let abortController = new AbortController();
    
    const generateInterpretation = async () => {
      if (!stats || !selectedSite) return;
      
      // Cancel any previous request
      abortController.abort();
      abortController = new AbortController();
      
      setInterpretationLoading(true);
      setAgentInsights([]); // Clear previous insights
      setCurrentInsightIndex(0); // Reset to first insight
      
      try {
        const analyticsContext = {
          siteName: selectedSite.name,
          siteUrl: selectedSite.url,
          period: period === 'range' ? date : period,
          visits: stats.visits || 0,
          uniqueVisitors: stats.uniqueVisitors || 0,
          pageViews: stats.pageViews || 0,
          bounceRate: stats.bounceRate || 0,
          avgDuration: stats.avgVisitDuration || 0,
          conversionRate: stats.conversionRate || 0,
          visitorTrend: visitorTrend,
          topPages: pageStats,
          pagemindUsage: pagemindUsage // Add PageMind usage data
        };

        // Add timeout to prevent hanging
        const timeoutId = setTimeout(() => abortController.abort(), 15000); // 15 second timeout
        
        try {
          const response = await authFetch('/agent-insights/web-analytics', {
            method: 'POST',
            signal: abortController.signal,
            body: JSON.stringify({
              context: analyticsContext,
              prompt: `Analyze this website's performance data and provide actionable insights. Focus on:
1. Traffic patterns and visitor engagement
2. Bounce rate and session duration analysis
3. Conversion opportunities
4. Top performing pages
5. PageMind chatbot impact on user engagement (if usage data is available)
6. Specific recommendations for improvement

IMPORTANT: Generate between 5 and 8 different insights. Each insight should cover a different aspect of the data.
Return as a JSON object with an "insights" array.`
            })
          });
          
          clearTimeout(timeoutId);

          if (response.ok) {
            const data = await response.json();
            console.log('[Web Analytics] Raw response:', data);
            console.log('[Web Analytics] Insights type:', typeof data.insights);
            console.log('[Web Analytics] Insights length:', data.insights?.length);
            
            // Handle different response formats
            if (data.insights && Array.isArray(data.insights)) {
              setAgentInsights(data.insights);
            } else if (data.interpretation) {
              // Fallback for old format
              setAgentInsights([{
                title: "Analysis",
                content: data.interpretation,
                type: "neutral"
              }]);
            } else {
              console.error('[Web Analytics] Unexpected response format:', data);
              setAgentInsights([]);
            }
          }
        } catch (innerError) {
          clearTimeout(timeoutId);
          throw innerError;
        }
      } catch (error) {
        // Don't log abort errors unless it's a timeout
        if (error.name === 'AbortError' && !abortController.signal.aborted) {
          console.log('[Web Analytics] Request timed out after 15 seconds');
        } else if (error.name !== 'AbortError') {
          console.error('Failed to generate interpretation:', error);
        }
        // Fallback to rule-based interpretation is already in the component
      } finally {
        if (!abortController.signal.aborted) {
          setInterpretationLoading(false);
        }
      }
    };

    generateInterpretation();
    
    // Cleanup function to abort request when component unmounts or dependencies change
    return () => {
      abortController.abort();
    };
  }, [stats, selectedSite, period, date, visitorTrend, pageStats, pagemindUsage]); // Regenerate when data changes

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  // If no site selected, show a prompt
  if (!selectedSite) {
    return (
      <div className="container mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Website Analytics</h1>
          <p className="text-muted-foreground mt-1">
            Real-time web analytics and visitor insights
          </p>
        </div>

        <Alert className="max-w-2xl mx-auto mt-8">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No Site Selected</AlertTitle>
          <AlertDescription>
            <p>Please select a site from the header to view analytics.</p>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Analytics</h1>
        <p className="text-muted-foreground mt-1">
          Comprehensive analytics for your website and chatbot interactions
        </p>
      </div>

      {/* Site information from global context */}
      <div className="mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Currently viewing analytics for:</CardDescription>
            <CardTitle className="text-lg">{selectedSite?.name || selectedSite?.url}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Tabs defaultValue="web-analytics" className="space-y-6">
        <TabsList>
          <TabsTrigger value="web-analytics">Web Analytics</TabsTrigger>
          <TabsTrigger value="chatbot-analytics">Chatbot Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="web-analytics" className="space-y-6">{/* Start Web Analytics Tab */}

      {/* Show error if stats couldn't be loaded */}
      {statsError && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Analytics</AlertTitle>
          <AlertDescription>
            Unable to load analytics data. This might be because the site hasn't been configured in Matomo yet, 
            or there's an issue with the API configuration.
          </AlertDescription>
        </Alert>
      )}

      <div className="mb-6 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <Select value={period === 'range' ? date : period} onValueChange={(value) => {
            // Handle range selections
            if (value === 'last7' || value === 'last30') {
              setPeriod('range');
              setDate(value);
            } else {
              setPeriod(value);
              // Update date based on period
              if (value === 'day') setDate('today');
              else if (value === 'week') setDate('week');
              else if (value === 'month') setDate('month');
              else if (value === 'year') setDate('year');
            }
          }}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Today</SelectItem>
              <SelectItem value="last7">Last 7 days</SelectItem>
              <SelectItem value="last30">Last 30 days</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Visits
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
            ) : (
              <p className="text-2xl font-bold">{stats?.visits?.toLocaleString() || '0'}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Unique Visitors
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
            ) : (
              <p className="text-2xl font-bold">
                {stats?.uniqueVisitors === '-' ? '-' : (stats?.uniqueVisitors?.toLocaleString() || '0')}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Page Views
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
            ) : (
              <p className="text-2xl font-bold">{stats?.pageViews?.toLocaleString() || '0'}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Bounce Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
            ) : (
              <p className="text-2xl font-bold">{stats?.bounceRate?.toFixed(1) || '0.0'}%</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Avg Duration
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
            ) : (
              <p className="text-2xl font-bold">{stats ? formatDuration(stats.avgVisitDuration || 0) : '0m 0s'}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4" />
              Conversion Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
            ) : (
              <p className="text-2xl font-bold">{stats?.conversionRate?.toFixed(1) || '0.0'}%</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Web Analytics Agent Interpretation */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">AI-Powered Analytics Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-4">
            {imageError ? (
              <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-blue-200 flex-shrink-0">
                <span className="text-blue-600 text-sm font-bold">WA</span>
              </div>
            ) : (
              <img 
                src="/crew-agents/webanalytics.png" 
                alt="Web Analytics Agent"
                className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                onError={() => setImageError(true)}
              />
            )}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">Web Analytics Agent</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex items-center gap-1"
                  onClick={() => {
                    navigate('/insights/hub/web-analyst');
                  }}
                >
                  <MessageCircle className="w-3 h-3" />
                  Let's talk
                </Button>
              </div>
              {interpretationLoading ? (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600 mb-2 flex items-center">
                    <span className="font-medium">Nathan, Web Analytics Agent</span>
                    <span className="ml-2">typing</span>
                    <span className="ml-1 inline-flex">
                      <span className="animate-bounce animation-delay-0">.</span>
                      <span className="animate-bounce animation-delay-100">.</span>
                      <span className="animate-bounce animation-delay-200">.</span>
                    </span>
                  </p>
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-full" />
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-5/6" />
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-4/6" />
                </div>
              ) : agentInsights.length > 1 ? (
                <div className="space-y-3">
                  <div className="relative">
                    <div className="flex items-start gap-3">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 flex-shrink-0 hover:bg-gray-100"
                        disabled={currentInsightIndex === 0}
                        onClick={() => setCurrentInsightIndex(prev => Math.max(0, prev - 1))}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      
                      <div className="flex-1 min-h-[60px]">
                        {agentInsights[currentInsightIndex] && (
                          <>
                            <h4 className="text-sm font-medium text-gray-700 mb-1">
                              {agentInsights[currentInsightIndex].title}
                            </h4>
                            <div 
                              className="text-sm text-gray-600 leading-relaxed"
                              dangerouslySetInnerHTML={{ __html: agentInsights[currentInsightIndex].content }}
                            />
                          </>
                        )}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 flex-shrink-0 hover:bg-gray-100"
                        disabled={currentInsightIndex === agentInsights.length - 1}
                        onClick={() => setCurrentInsightIndex(prev => Math.min(agentInsights.length - 1, prev + 1))}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="flex items-center justify-center mt-3 gap-1">
                      {agentInsights.map((_, index) => (
                        <button
                          key={index}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            index === currentInsightIndex ? 'bg-primary' : 'bg-gray-300'
                          }`}
                          onClick={() => setCurrentInsightIndex(index)}
                        />
                      ))}
                    </div>
                    
                    <p className="text-xs text-center text-gray-500 mt-2">
                      {currentInsightIndex + 1} of {agentInsights.length} insights
                    </p>
                  </div>
                  
                  <p className="text-xs text-gray-500 italic text-right">
                    — Nathan, Web Analytics Agent
                  </p>
                </div>
              ) : agentInsights.length === 1 ? (
                <div className="space-y-3">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">
                      {agentInsights[0].title}
                    </h4>
                    <div 
                      className="text-sm text-gray-600 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: agentInsights[0].content }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 italic text-right">
                    — Nathan, Web Analytics Agent
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-600 leading-relaxed">
                  Based on your analytics data: You have {stats?.visits?.toLocaleString() || '0'} visits{stats?.uniqueVisitors !== '-' ? ` with ${stats?.uniqueVisitors?.toLocaleString() || '0'} unique visitors` : ''}. 
                  Your bounce rate is {stats?.bounceRate?.toFixed(1) || '0'}% and average session duration is {stats?.avgVisitDuration || '0'}s. 
                  {stats?.bounceRate && stats.bounceRate > 60 ? 
                    " The high bounce rate suggests visitors aren't finding what they're looking for. Consider improving your landing pages and content relevance." :
                    stats?.bounceRate && stats.bounceRate < 40 ?
                    " Your low bounce rate indicates good engagement. Visitors are exploring multiple pages." :
                    " Your bounce rate is within normal range."}
                  {stats?.avgDuration && stats.avgDuration < 60 ?
                    " The short session duration indicates users might be leaving quickly. Focus on creating more engaging content." :
                    stats?.avgDuration && stats.avgDuration > 180 ?
                    " Great session duration! Users are spending quality time on your site." :
                    ""}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Visitor Trend Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Visitor Trend</CardTitle>
            <CardDescription>
              {period === 'day' ? 'Daily visits and unique visitors (last 7 days)' :
               period === 'week' ? 'Daily visits and unique visitors for this week' :
               period === 'month' ? 'Daily visits and unique visitors for this month' :
               period === 'year' ? 'Monthly visits and unique visitors for this year' :
               date === 'last7' ? 'Daily visits and unique visitors (last 7 days)' :
               'Daily visits and unique visitors (last 30 days)'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {trendLoading ? (
              <div className="h-[300px] w-full bg-gray-100 rounded animate-pulse" />
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={visitorTrend}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis 
                      dataKey="date" 
                      className="text-xs"
                      tickFormatter={(value) => {
                        // Parse YYYY-MM-DD format
                        const parts = value.split('-');
                        if (parts.length === 3) {
                          const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        }
                        return value;
                      }}
                    />
                    <YAxis className="text-xs" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--background))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px',
                        padding: '8px 12px'
                      }}
                      labelFormatter={(value) => {
                        // Parse YYYY-MM-DD format
                        const parts = value.split('-');
                        if (parts.length === 3) {
                          const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                          return date.toLocaleDateString('en-US', { 
                            weekday: 'short',
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric'
                          });
                        }
                        return value;
                      }}
                      formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      iconType="line"
                      wrapperStyle={{
                        paddingTop: '20px',
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="nb_visits" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={2}
                      name="Visits"
                      dot={{ fill: 'hsl(var(--primary))', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, strokeWidth: 0 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="nb_uniq_visitors" 
                      stroke="hsl(var(--chart-2))" 
                      strokeWidth={2}
                      name="Unique Visitors"
                      dot={{ fill: 'hsl(var(--chart-2))', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, strokeWidth: 0 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Pages */}
        <Card>
          <CardHeader>
            <CardTitle>Top Pages</CardTitle>
            <CardDescription>Most visited pages on your site</CardDescription>
          </CardHeader>
          <CardContent>
            {pagesLoading ? (
              <div className="h-[300px] w-full bg-gray-100 rounded animate-pulse" />
            ) : (
              <div className="space-y-4">
                {pageStats?.slice(0, 5).map((page, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium truncate flex-1 mr-4">{page.label}</span>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-muted-foreground">{page.nb_visits} visits</span>
                        <span className="text-muted-foreground">{page.bounce_rate}</span>
                      </div>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${(page.nb_visits / (pageStats[0]?.nb_visits || 1)) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* PageMind Usage Analysis */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>PageMind Usage Analysis</CardTitle>
          <CardDescription>Compare visits that used PageMind vs those that didn't</CardDescription>
        </CardHeader>
        <CardContent>
          {usageLoading ? (
            <div className="h-[200px] w-full bg-gray-100 rounded animate-pulse" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left pb-3 font-medium">Metric</th>
                    <th className="text-right pb-3 font-medium">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="py-3">Visits that used PageMind</td>
                    <td className="text-right py-3 font-mono">
                      {pagemindUsage?.visitsWithPagemind.toLocaleString() || '0'}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3">Total Visits</td>
                    <td className="text-right py-3 font-mono">
                      {pagemindUsage?.totalVisits.toLocaleString() || '0'}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3">PageMind Usage Rate</td>
                    <td className="text-right py-3 font-mono">
                      {pagemindUsage?.usagePercent.toFixed(1) || '0'}%
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3">Avg Duration (with PageMind)</td>
                    <td className="text-right py-3 font-mono">
                      {formatDuration(pagemindUsage?.avgDurationWithPagemind || 0)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3">Avg Duration (without PageMind)</td>
                    <td className="text-right py-3 font-mono">
                      {formatDuration(pagemindUsage?.avgDurationWithoutPagemind || 0)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3">Duration Change</td>
                    <td className="text-right py-3">
                      {(pagemindUsage?.avgDurationWithPagemind === 0 || pagemindUsage?.avgDurationWithoutPagemind === 0) ? (
                        <span className="font-mono text-muted-foreground">N.A.</span>
                      ) : (
                        <span className={`font-mono ${
                          (pagemindUsage?.durationChangePercent || 0) > 0 
                            ? 'text-green-600 dark:text-green-400' 
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {(pagemindUsage?.durationChangePercent || 0) > 0 ? '+' : ''}
                          {pagemindUsage?.durationChangePercent?.toFixed(1) || '0'}%
                        </span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="mt-6 text-center text-xs text-muted-foreground">
        Real-time visitor tracking and analytics
      </div>

        </TabsContent>{/* End Web Analytics Tab */}

        <TabsContent value="chatbot-analytics" className="space-y-6">
          {selectedSite ? (
            <ChatbotAnalyticsEmbed siteId={selectedSite._id} />
          ) : (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>No Site Selected</AlertTitle>
              <AlertDescription>
                Please select a site from the dropdown above to view chatbot analytics.
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MatomoAnalytics;